'use client';
import React, { useEffect, useRef } from 'react';

const featuredCards = [
{
  category: 'COSMOLOGY',
  title: 'The Inception of the Universe',
  description: 'From a singular point of infinite density, everything that exists exploded into being. But what came before the Big Bang?',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b8b3fb74-1772068377607.png",
  alt: 'Deep space nebula with swirling cosmic gases and distant galaxies representing the birth of the universe',
  tag: 'THEORY'
},
{
  category: 'BIOLOGY',
  title: 'How Life Began from Chemistry',
  description: 'The leap from inorganic molecules to self-replicating life is the most profound transition in Earth\'s history.',
  image: "https://images.unsplash.com/photo-1684364618942-5da19527a176",
  alt: 'Microscopic view of cellular structures and DNA strands showing the molecular basis of life',
  tag: 'SCIENCE'
},
{
  category: 'PHILOSOPHY',
  title: 'The First Thought: Origin of Consciousness',
  description: 'Did consciousness emerge from matter, or was it always there? The hard problem of consciousness remains unsolved.',
  image: "https://images.unsplash.com/photo-1503330830793-294b84e5a279",
  alt: 'Abstract visualization of neural networks and brain activity representing the mystery of consciousness',
  tag: 'OBSERVATION'
},
{
  category: 'MATERIALS',
  title: 'Everything Is Connected: The Web of Matter',
  description: 'Every atom in your body was forged in a star. The iron in your blood and the carbon in your bones share a cosmic origin.',
  image: "https://images.unsplash.com/photo-1546350438-5fa44f3e5fd3",
  alt: 'Interconnected network of glowing nodes representing the web of matter and atomic connections',
  tag: 'STORY'
},
{
  category: 'LANGUAGE',
  title: 'The First Word: How Language Was Born',
  description: 'Language is humanity\'s most powerful tool. But how did the first symbolic communication emerge from primal sounds?',
  image: "https://images.unsplash.com/photo-1615469619480-1a7e77deb56c",
  alt: 'Ancient cave paintings and symbols representing the earliest forms of human language and communication',
  tag: 'HISTORY'
},
{
  category: 'PHYSICS',
  title: 'The Inception of Time Itself',
  description: 'Time may not be a fundamental feature of reality. Some physicists argue time is an emergent illusion of entropy.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_143ddfe01-1767663672056.png",
  alt: 'Abstract clock and time visualization with light trails representing the concept of time as a physical phenomenon',
  tag: 'THEORY'
}];


export default function HeroSection() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    const particles: {x: number;y: number;vx: number;vy: number;size: number;opacity: number;color: string;}[] = [];
    const colors = ['#e01a2e', '#ffffff', '#ff4d5e', '#ff8a94'];

    for (let i = 0; i < 80; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        size: Math.random() * 2 + 0.5,
        opacity: Math.random() * 0.6 + 0.1,
        color: colors[Math.floor(Math.random() * colors.length)]
      });
    }

    let animId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.opacity;
        ctx.fill();
      });
      ctx.globalAlpha = 1;
      animId = requestAnimationFrame(animate);
    };
    animate();

    const handleResize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    window.addEventListener('resize', handleResize);
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <section className="relative bg-[#070a14] pt-[72px] overflow-hidden">
      {/* Animated particle canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ zIndex: 1 }} />
      

      {/* Radial starburst gradient background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
          'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(224,26,46,0.08) 0%, rgba(224,26,46,0.03) 40%, transparent 70%)',
          zIndex: 2
        }} />
      

      {/* Hero Content */}
      <div className="relative max-w-[1280px] mx-auto px-6 pt-16 pb-12" style={{ zIndex: 3 }}>
        <div className="max-w-3xl">
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-block w-8 h-[2px] bg-[#e01a2e]" />
            <span
              className="text-[#e01a2e] text-xs font-semibold tracking-[0.2em] uppercase"
              style={{ fontFamily: 'Roboto, sans-serif' }}>
              
              Explore the Origin of Everything
            </span>
          </div>
          <h1
            className="text-white font-bold leading-tight mb-5"
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: 'clamp(32px, 5vw, 52px)',
              lineHeight: '1.1'
            }}>
            
            Every Beginning Has a{' '}
            <span
              className="relative inline-block"
              style={{
                color: '#e01a2e',
                textShadow: '0 0 40px rgba(224,26,46,0.3)'
              }}>
              
              Story.
            </span>
          </h1>
          <h2
            className="text-[#a0aec0] font-normal mb-8 max-w-2xl"
            style={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: 'clamp(15px, 2vw, 20px)',
              lineHeight: '1.6',
              fontWeight: 400
            }}>
            
            We explore the inception of everything — how every material, idea, and phenomenon is
            interconnected. Theories, stories, observations, and deep-dive articles about the origins
            that shape our reality.
          </h2>
          <div className="flex flex-wrap gap-3">
            <a
              href="#articles"
              className="inline-flex items-center gap-2 bg-[#e01a2e] hover:bg-[#c0152a] text-white font-semibold px-6 py-3 rounded-[3px] transition-all duration-200 text-[14px]"
              style={{ fontFamily: 'Roboto, sans-serif' }}>
              
              Explore Articles
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </a>
            <a
              href="#topics"
              className="inline-flex items-center gap-2 border border-[#2d3748] hover:border-[#e01a2e] text-[#c8d0e0] hover:text-white font-semibold px-6 py-3 rounded-[3px] transition-all duration-200 text-[14px]"
              style={{ fontFamily: 'Roboto, sans-serif' }}>
              
              Browse Topics
            </a>
          </div>
        </div>
      </div>

      {/* Featured Cards Row */}
      <div className="relative max-w-[1280px] mx-auto px-6 pb-12" style={{ zIndex: 3 }}>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {featuredCards.map((card, i) =>
          <FeaturedCard key={i} card={card} />
          )}
        </div>
      </div>

      {/* Bottom border line */}
      <div className="h-px bg-gradient-to-r from-transparent via-[#e01a2e] to-transparent opacity-30" />
    </section>);

}

function FeaturedCard({ card }: {card: typeof featuredCards[0];}) {
  return (
    <a
      href="#articles"
      className="group relative block overflow-hidden rounded-sm bg-[#0d1120] border border-[#1e2a3a] hover:border-[#e01a2e] transition-all duration-300 cursor-pointer"
      style={{ minHeight: '200px' }}>
      
      {/* Image */}
      <div className="relative h-32 overflow-hidden">
        <img
          src={card.image}
          alt={card.alt}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
        
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d1120] via-transparent to-transparent" />
        <span
          className="absolute top-2 left-2 text-[9px] font-bold tracking-widest px-1.5 py-0.5 rounded-sm"
          style={{
            fontFamily: 'Roboto, sans-serif',
            background: 'rgba(224,26,46,0.85)',
            color: '#fff'
          }}>
          
          {card.tag}
        </span>
      </div>
      {/* Content */}
      <div className="p-3">
        <span
          className="block text-[9px] font-bold tracking-widest text-[#e01a2e] mb-1 uppercase"
          style={{ fontFamily: 'Roboto, sans-serif' }}>
          
          {card.category}
        </span>
        <h3
          className="text-white text-[11px] font-bold leading-tight group-hover:text-[#e01a2e] transition-colors duration-200"
          style={{ fontFamily: 'Montserrat, sans-serif' }}>
          
          {card.title}
        </h3>
      </div>
    </a>);

}